(ns nosco.shared.form-fields
  (:require
   [clojure.string :as string]
   #?@(:clj
       [[nosco.database :as database]
        [nosco.util :as util]
        [nosco.util.date-format :as date-format]
        [nosco.util.html :as html]])
   #?@(:cljs
       [[nosco.controllers :as controllers]
        [nosco.i18n.translate :as t]
        [nosco.icons2 :as icons2]])))

;; =============================================================================
;; ADDING NEW FIELD TYPES - REQUIRED UPDATES
;; =============================================================================
;;
;; When adding a new field type, you MUST update these locations that cannot be
;; easily discovered by searching for namespaces that import form-fields:
;;
;; 1. THIS FILE (nosco.shared.form_fields.cljc):
;;    - Add entry to `field-types` registry below with all metadata
;;    - Consider category (:title, :text, :number, :date, :choice, :users, :info)
;;    - Define :accepts rules for type conversions
;;    - Add predicates if needed (e.g., number-type? includes NUMBER, SCALE, SLIDER)
;;    - Update multimethods: value-blank?, value->text, string->template-value
;;    - Update idea-template->markdown if needed (CLJ-only section)
;;
;; 2. nosco.specs.templates (src/nosco/specs/templates.clj):
;;    Does NOT import form-fields, has Clojure specs:
;;    - ::type spec definition (add mongo type string)
;;    - Add value spec if needed (e.g., :number/values, :date/values)
;;    - idea-template-item-type multimethod (define validation for values)
;;
;; 3. PostgreSQL migrations (resources/postgres/migrations/):
;;    SQL functions need manual updates:
;;    - Create new migration file YYYYMMDDHHMMSS-<description>.sql
;;    - Update template_value function to handle new type
;;    - Consider other SQL functions that extract template values
;;
;; 4. GraphQL Schema (resources/nosco-schema.edn):
;;    Enum definitions need manual updates:
;;    - Add to :IdeaFieldType enum
;;    - Add to :EvaluationFieldType enum (if applicable)
;;    - Update :IdeaInputTemplateItem if new value property needed
;;
;; DISCOVERY STRATEGY:
;; For all other locations (that import form-fields), use "find references" on
;; existing field type keywords like :NUMBER, :TEXTAREA, etc. This will show you:
;; - Database layer functions (extract-input-value, fill-in-template-item, etc.)
;; - GraphQL resolvers (idea-template-item-value, etc.)
;; - Report generation (Excel, PDF exports)
;; - Frontend components (editors, viewers, form state)
;; - Automation and import logic
;;
;; Most files that import form-fields already use predicates (number-type?,
;; option-field-type?, etc.) and will automatically handle new types within
;; those categories. Only add explicit cases where truly type-specific logic
;; is needed (e.g., rendering, formatting, data conversion).
;;
;; TESTING:
;; - test/nosco/test/integration/graphql/form_field_types_test.clj (comprehensive tests)
;; - test/nosco/test/unit/workflow_test.clj (automation tests if applicable)
;; - Test: create, edit, view, view in table, import, excel, pdf, formulas, type conversions, automation
;;

;; =============================================================================
;; Field Types Registry
;; =============================================================================
;; Central registry of all form field types.
;; Add new field types here - one map entry per type.
;; This consolidates the 5 case statements (labels, icons, help, etc.)

(def field-types
  "Central registry of all form field types.
   Each entry contains:
   - :mongo-type   - MongoDB field type string
   - :value-type   - GraphQL value type keyword
   - :category     - Category keyword for grouping (:title, :text, :number, :date, :choice, :users)
   - :accepts      - Set of field types this type can accept values from (for conversion)
   - :label        - Display label (cljs only)
   - :add-label    - Label for 'Add X' action (cljs only)
   - :edit-label   - Label for 'Edit X' action (cljs only)
   - :help         - Help text description (cljs only)
   - :icon         - Icon component (cljs only)

   Accepts rules:
   - :accepts defines which source types can be converted into this target type
   - Richer formats can accept simpler ones (e.g., textarea accepts textline)
   - Text fields can accept date/number (converted to string)
   - Multi-choice can accept single-choice"
  {:TITLE
   {:mongo-type   "title"
    :value-type   :IdeaTemplateTextField
    :category     :title
    :accepts      #{:TITLE}
    #?@(:cljs
        [:label      (t/t "Title")
         :add-label  (t/t "Add title")
         :edit-label (t/t "Edit title")
         :help       (t/t "A title for the idea")
         :icon       icons2/Title-large])}

   :TEXTAREA
   {:mongo-type   "textarea"
    :value-type   :IdeaTemplateTextField
    :category     :text
    :accepts      #{:TEXTAREA :TEXTLINE :DATE :NUMBER :SCALE :SLIDER :USERS
                    :DROPDOWN :SELECT_SINGLE :SELECT_MULTIPLE :SELECT_MULTIPLE_EXTENSIBLE}
    #?@(:cljs
        [:label      (t/t "Multi-line")
         :add-label  (t/t "Add multi-line")
         :edit-label (t/t "Edit multi-lines")
         :help       (t/t "Good for long text")
         :icon       icons2/AlignLeft-large])}

   :TEXTLINE
   {:mongo-type   "text"
    :value-type   :IdeaTemplateTextField
    :category     :text
    :accepts      #{:TEXTLINE :TEXTAREA :DATE :NUMBER :SCALE :SLIDER :USERS
                    :DROPDOWN :SELECT_SINGLE :SELECT_MULTIPLE :SELECT_MULTIPLE_EXTENSIBLE}
    #?@(:cljs
        [:label      (t/t "Single line")
         :add-label  (t/t "Add single line")
         :edit-label (t/t "Edit single line")
         :help       (t/t "Good for short text")
         :icon       icons2/Short-text-large])}

   :DATE
   {:mongo-type   "date"
    :value-type   :IdeaTemplateDateField
    :category     :date
    :accepts      #{:DATE :NUMBER :TEXTLINE :TEXTAREA}
    #?@(:cljs
        [:label      (t/t "Date")
         :add-label  (t/t "Add date")
         :edit-label (t/t "Edit date")
         :help       (t/t "Select or fill in a date")
         :icon       icons2/Calendar-large])}

   :NUMBER
   {:mongo-type   "number"
    :value-type   :IdeaTemplateNumberField
    :category     :number
    :accepts      #{:NUMBER :SCALE :SLIDER :TEXTLINE :TEXTAREA :DATE}
    #?@(:cljs
        [:label      (t/t "Number")
         :add-label  (t/t "Add number")
         :edit-label (t/t "Edit number")
         :help       (t/t "For numbers or mathematical formulas")
         :icon       icons2/Number-large])}

   :SCALE
   {:mongo-type   "scale"
    :value-type   :IdeaTemplateNumberField
    :category     :number
    :accepts      #{:SCALE :NUMBER :TEXTLINE :TEXTAREA
                    :DROPDOWN :SELECT_SINGLE :SELECT_MULTIPLE :SELECT_MULTIPLE_EXTENSIBLE}
    #?@(:cljs
        [:label      (t/t "Scale")
         :add-label  (t/t "Add scale")
         :edit-label (t/t "Edit scale")
         :help       (t/t "Choose a number from a scale, e.g. 1-10")
         :icon       icons2/Steppers-large])}

   :SLIDER
   {:mongo-type   "slider"
    :value-type   :IdeaTemplateNumberField
    :category     :number
    :accepts      #{:SLIDER :NUMBER :TEXTLINE :TEXTAREA}
    #?@(:cljs
        [:label      (t/t "Slider")
         :add-label  (t/t "Add slider")
         :edit-label (t/t "Edit slider")
         :help       (t/t "Choose a number from a sliding range, e.g. 0-100%")
         :icon       icons2/Slider-large])}

   :DROPDOWN
   {:mongo-type   "select"
    :value-type   :IdeaTemplateSelectionField
    :category     :single-choice
    :accepts      #{:DROPDOWN :SELECT_SINGLE :SCALE}
    #?@(:cljs
        [:label      (t/t "Dropdown")
         :add-label  (t/t "Add dropdown")
         :edit-label (t/t "Edit dropdown")
         :help       (t/t "Select an option from a long list")
         :icon       icons2/DropdownSm-large])}

   :SELECT_SINGLE
   {:mongo-type   "radio"
    :value-type   :IdeaTemplateSelectionField
    :category     :single-choice
    :accepts      #{:SELECT_SINGLE :DROPDOWN :SCALE}
    #?@(:cljs
        [:label      (t/t "Options")
         :add-label  (t/t "Add options")
         :edit-label (t/t "Edit options")
         :help       (t/t "Select an option from a short list")
         :icon       icons2/Check-large])}

   :SELECT_MULTIPLE
   {:mongo-type   "checkbox"
    :value-type   :IdeaTemplateSelectionField
    :category     :multi-choice
    :accepts      #{:SELECT_MULTIPLE :SELECT_MULTIPLE_EXTENSIBLE
                    :SELECT_SINGLE :DROPDOWN :SCALE}
    #?@(:cljs
        [:label      (t/t "Checkbox")
         :add-label  (t/t "Add checkbox")
         :edit-label (t/t "Edit checkbox")
         :help       (t/t "Select one or more options from a short list")
         :icon       icons2/Evaluate-large])}

   :SELECT_MULTIPLE_EXTENSIBLE
   {:mongo-type   "checkbox_extensible"
    :value-type   :IdeaTemplateSelectionField
    :category     :multi-choice
    :accepts      #{:SELECT_MULTIPLE_EXTENSIBLE :SELECT_MULTIPLE
                    :SELECT_SINGLE :DROPDOWN :SCALE}
    #?@(:cljs
        [:label      (t/t "Tags")
         :add-label  (t/t "Add tags")
         :edit-label (t/t "Edit tags")
         :help       (t/t "Select one or more options from a list or add new options")
         :icon       icons2/Tags-large])}

   :USERS
   {:mongo-type   "users"
    :value-type   :IdeaTemplateUserField
    :category     :users
    :accepts      #{:USERS}
    #?@(:cljs
        [:label      (t/t "People")
         :add-label  (t/t "Add people")
         :edit-label (t/t "Edit people")
         :help       (t/t "Select people, e.g. co-authors or team members")
         :icon       icons2/UserMultiple-large])}

   :IDEAS
   {:mongo-type   "ideas"
    :value-type   :IdeaTemplateIdeasField
    :category     :ideas
    :accepts      #{:IDEAS}
    #?@(:cljs
        [:label      (t/t "Ideas")
         :add-label  (t/t "Add ideas")
         :edit-label (t/t "Edit ideas")
         :help       (t/t "Link related ideas from this or another channel")
         :icon       icons2/Lightbulb-large])}

   :INFO
   {:mongo-type   "info"
    :value-type   :IdeaTemplateInfoField
    :category     :info
    :accepts      #{:INFO}
    #?@(:cljs
        [:label      (t/t "Info")
         :add-label  (t/t "Add info")
         :edit-label (t/t "Edit info")
         :help       (t/t "Provide authors with guidance")
         :icon       icons2/Info-large])}})

;; =============================================================================
;; MongoDB Type Mapping (derived from registry)
;; =============================================================================

(def mongo->graphql-type
  "Map from MongoDB type string to GraphQL type keyword.
   Derived from the :mongo-type entries in field-types registry."
  (into {}
        (map (fn [[gql-type {:keys [mongo-type]}]]
               [mongo-type gql-type]))
        field-types))

;; =============================================================================
;; Derived Category Sets (work in both clj and cljs)
;; =============================================================================

(defn field-types-with-category
  "Returns set of field type keywords that have the given category."
  [category]
  (into #{}
        (comp (filter #(= (:category (val %)) category))
              (map key))
        field-types))

(def ^:private  title-types
  "#{:TITLE}"
  (field-types-with-category :title))

(def ^:private  text-types
  "#{:TEXTAREA :TEXTLINE}"
  (field-types-with-category :text))

(def ^:private  number-types
  "#{:NUMBER :SCALE :SLIDER}"
  (field-types-with-category :number))

(def ^:private  date-types
  "#{:DATE}"
  (field-types-with-category :date))

(def ^:private single-choice-types
  "#{:DROPDOWN :SELECT_SINGLE}"
  (field-types-with-category :single-choice))

(def ^:private multi-choice-types
  "#{:SELECT_MULTIPLE :SELECT_MULTIPLE_EXTENSIBLE}"
  (field-types-with-category :multi-choice))

(def ^:private choice-types
  "#{:DROPDOWN :SELECT_SINGLE :SELECT_MULTIPLE :SELECT_MULTIPLE_EXTENSIBLE}
   Union of single-choice and multi-choice types."
  (into single-choice-types multi-choice-types))

(def ^:private users-types
  "#{:USERS}"
  (field-types-with-category :users))

(def ^:private ideas-types
  "#{:IDEAS}"
  (field-types-with-category :ideas))

(def category-colors
  "Category -> nosco colour token used to tint the field type indicator.
   Field types of the same category always share a colour, and the 'add a field'
   gallery groups are built so that a group never mixes categories with different
   colours. See `field-type-color` and `field-type-groups`."
  {:title         "accent-purple-200"
   :text          "accent-teal-250"
   :info          "accent-teal-250"
   :single-choice "accent-lime-200"
   :multi-choice  "accent-lime-200"
   :number        "accent-orange-200"
   :date          "accent-magenta-200"
   :users         "accent-magenta-200"
   :ideas         "accent-magenta-200"})

;; =============================================================================
;; Available Field Types (feature-flag aware)
;; =============================================================================

#?(:cljs
   (defn field-type-groups
     "The 'add a field' gallery, as ordered groups of field types.
      Each group has:
      - :key    - group identifier
      - :label  - group heading
      - :color  - nosco colour token for the field type indicator background
      - :types  - the field types in the group, in display order

      :IDEAS is gated behind the :ideas-field flag. Gating is at the gallery only --
      the field-types registry, resolvers, storage, SQL and rendering stay
      unconditional, so already-added fields keep working regardless of the flag.
      Uses syntax-quote with unquote-splicing for explicit ordering."
     []
     (let [ideas? (controllers/has-flag? :ideas-field)]
       [{:key   :text
         :label (t/t "Text")
         :color (category-colors :text)
         :types [:TEXTLINE
                 :TEXTAREA
                 :INFO]}
        {:key   :choice
         :label (t/t "Choice")
         :color (category-colors :single-choice)
         :types [:DROPDOWN
                 :SELECT_SINGLE
                 :SELECT_MULTIPLE
                 :SELECT_MULTIPLE_EXTENSIBLE]}
        {:key   :number
         :label (t/t "Number & ratings")
         :color (category-colors :number)
         :types [:NUMBER
                 :SLIDER
                 :SCALE]}
        {:key   :other
         :label (t/t "Other")
         :color (category-colors :users)
         :types `[:USERS
                  ~@(when ideas? [:IDEAS])
                  :DATE]}])))

#?(:cljs
   (defn available-field-types
     "All available field types for forms (the 'add a field' gallery), in display order.
      Derived from `field-type-groups`, so ordering and feature-flag gating live in one
      place and a new field type can't be added to one without the other."
     []
     (into [] (mapcat :types) (field-type-groups))))

;; =============================================================================
;; Type Normalization
;; =============================================================================

(def ^:private graphql-type-strings
  "Map from GraphQL type strings to keywords.
   Derived from the registry keys."
  (into {}
        (map (fn [k] [(name k) k]))
        (keys field-types)))

(defn ->type
  "Normalizes a field type to a keyword. Accepts:
   - Keywords (validated against registry)
   - GraphQL type strings like \"TEXTAREA\", \"DROPDOWN\"
   - MongoDB type strings like \"textarea\", \"checkbox\"
   Throws for unknown types."
  [t]
  (cond
    (nil? t)
    nil

    (keyword? t)
    (if (contains? field-types t)
      t
      (throw (ex-info (str "Unrecognized field type: " t) {:field-type t})))

    :else
    (or (get graphql-type-strings t)
        (get mongo->graphql-type t)
        (throw (ex-info (str "Unrecognized field type: " t) {:field-type t})))))

;; =============================================================================
;; Registry-Based Label/Icon/Help Accessors (cljs only)
;; =============================================================================

#?(:cljs
   (defn field-type-label
     "Returns the display label for a field type."
     [type]
     (get-in field-types [(->type type) :label])))

#?(:cljs
   (defn add-field-type-label
     "Returns the 'Add X' label for a field type.
      Use this instead of calling to-lowercase to take into account german noun capitalization."
     [type]
     (get-in field-types [(->type type) :add-label])))

#?(:cljs
   (defn edit-field-type-label
     "Returns the 'Edit X' label for a field type.
      Use this instead of calling to-lowercase to take into account german noun capitalization."
     [type]
     (get-in field-types [(->type type) :edit-label])))

#?(:cljs
   (defn field-type-help
     "Returns the help text for a field type."
     [type]
     (get-in field-types [(->type type) :help])))

#?(:cljs
   (defn field-type-icon
     "Returns the icon component for a field type."
     [type]
     (get-in field-types [(->type type) :icon])))

(defn field-type-color
  "Returns the nosco colour token used to tint the field type indicator, e.g.
   \"accent-teal-250\". Derived from the type's `:category` - see `category-colors`."
  [type]
  (category-colors (get-in field-types [(->type type) :category])))

;; =============================================================================
;; Option/Choice Field Helpers
;; =============================================================================

(defn title-type?
  "Returns true if the field type is a title field."
  [field-type]
  (contains? title-types (->type field-type)))

(defn text-type?
  "Returns true if the field type is a text-based field (textline, textarea)."
  [field-type]
  (contains? text-types (->type field-type)))

(defn number-type?
  "Returns true if the field type is a number-based field (number, scale, slider)."
  [field-type]
  (contains? number-types (->type field-type)))

(defn date-type?
  "Returns true if the field type is a date-based field."
  [field-type]
  (contains? date-types (->type field-type)))

(defn option-field-type?
  "Returns true if the field type is an option-based field (dropdown, select, etc.)"
  [field-type]
  (contains? choice-types (->type field-type)))

(defn single-choice?
  "Returns true if the field type is a single-choice field (dropdown, radio)."
  [field-type]
  (contains? single-choice-types (->type field-type)))

(defn multi-choice?
  "Returns true if the field type is a multi-choice field (checkbox, tags)."
  [field-type]
  (contains? multi-choice-types (->type field-type)))

(defn users-type?
  "Returns true if the field type is a users selection field."
  [field-type]
  (contains? users-types (->type field-type)))

(defn ideas-type?
  "Returns true if the field type is an ideas selection field."
  [field-type]
  (contains? ideas-types (->type field-type)))

(defn info-type?
  "Returns true if the field type is an info field (display-only, no user input)."
  [field-type]
  (= :INFO (->type field-type)))

(defn migration-compatible?
  "Returns true if a value from source-type can be migrated into target-type,
   per the target type's :accepts set."
  [source-type target-type]
  (contains? (get-in field-types [(->type target-type) :accepts])
             (->type source-type)))

(defn scale-type?
  "Returns true if the field type is a scale field."
  [field-type]
  (= :SCALE (->type field-type)))

(defn slider-type?
  "Returns true if the field type is a slider field."
  [field-type]
  (= :SLIDER (->type field-type)))

(defn colored-number-type?
  "Returns true if the field type is a number field with colors (scale or slider)."
  [field-type]
  (contains? #{:SCALE :SLIDER} (->type field-type)))

(defn get-scale-slider-config
  "Extract the color config from a scale or slider field's settings.
   Returns the config map (with :minColor, :midColor, :maxColor, :min, :max) or nil."
  [field field-type]
  (case (->type field-type)
    :SCALE (get-in field [:settings :scale])
    :SLIDER (get-in field [:settings :slider])
    nil))

(defn scale-step-label
  "Look up the per-step label for a numeric value in a scale field.
   config is the map from (get-in field [:settings :scale]).
   Returns the label string, or nil if no label is configured for that value."
  [config number-value]
  (when (and config number-value (seq (:labels config)))
    (some (fn [{:keys [value label]}]
            (when (== value number-value) label))
          (:labels config))))

;; =============================================================================
;; Type Compatibility Helpers (for consolidate-ideas feature)
;; =============================================================================

(defn accepts-types
  "Returns the set of field types that the given target-type can accept values from.
   This is read directly from the :accepts key in the registry."
  [target-type]
  (get-in field-types [(->type target-type) :accepts] #{}))

(defn compatible-target-types
  "Returns a set of field types that a source field type can be merged into.
   Derived from the :accepts registry entries - finds all types that accept the source.

   Examples:
   - TEXTLINE can go into TEXTAREA (textarea accepts textline)
   - DATE/NUMBER can go into TEXTLINE/TEXTAREA (converted to string)
   - SELECT_SINGLE can go into SELECT_MULTIPLE (multi accepts single)"
  [source-type]
  (let [source (->type source-type)]
    (into #{}
          (comp (filter (fn [[_type-key type-data]]
                          (contains? (:accepts type-data) source)))
                (map key))
          field-types)))

(defn compatible-target-type?
  "Returns true if source-type can be merged into target-type."
  [source-type target-type]
  (contains? (compatible-target-types source-type) (->type target-type)))

(defn requires-type-conversion?
  "Returns true if merging source-type into target-type requires value conversion.
   Conversion is needed when the value-type changes between source and target.
   E.g., NUMBER -> TEXTLINE changes from :IdeaTemplateNumberField to :IdeaTemplateTextField."
  [source-type target-type]
  (let [source (->type source-type)
        target (->type target-type)
        source-value-type (get-in field-types [source :value-type])
        target-value-type (get-in field-types [target :value-type])]
    (and (compatible-target-type? source target)
         (not= source-value-type target-value-type))))

#?(:cljs
   (defn type-conversion-warning
     "Returns a warning message if type conversion is needed, nil otherwise.
      The warning describes what kind of data transformation will occur."
     [source-type target-type]
     (when (requires-type-conversion? source-type target-type)
       (let [source (->type source-type)
             target (->type target-type)
             source-value-type (get-in field-types [source :value-type])
             target-value-type (get-in field-types [target :value-type])]
         (cond
           ;; Date to text
           (and (= source-value-type :IdeaTemplateDateField)
                (= target-value-type :IdeaTemplateTextField))
           (t/t "Date will be converted to text")

           ;; Number to text
           (and (= source-value-type :IdeaTemplateNumberField)
                (= target-value-type :IdeaTemplateTextField))
           (t/t "Number will be converted to text")

           ;; Generic fallback for any other value-type conversion
           :else
           (t/t "Values will be converted"))))))

;; =============================================================================
;; Value-Type Multimethods
;; =============================================================================
;; These dispatch on (:__typename value) which is the GraphQL union member.
;; Multiple field types can share the same value type.
;; Note: Frontend receives __typename as string, backend as keyword.

(defn- ->typename
  "Normalizes __typename to keyword for multimethod dispatch.
   Handles both string (frontend) and keyword (backend) forms."
  [value]
  (let [t (:__typename value)]
    (if (keyword? t) t (keyword t))))

(defmulti value-blank?
  "Returns true if the template field value is considered blank/empty.
   Dispatch on :__typename (keyword in backend, string in frontend)."
  ->typename)

(defmethod value-blank? :IdeaTemplateTextField [value]
  (string/blank? (:html value)))

(defmethod value-blank? :IdeaTemplateNumberField [value]
  (nil? (:number value)))

(defmethod value-blank? :IdeaTemplateDateField [value]
  (nil? (:date value)))

(defmethod value-blank? :IdeaTemplateUserField [value]
  (empty? (:users value)))

(defmethod value-blank? :IdeaTemplateIdeasField [value]
  (empty? (:ideas value)))

(defmethod value-blank? :IdeaTemplateSelectionField [value]
  (not (some :selected (:options value))))

(defmethod value-blank? :IdeaTemplateInfoField [_value]
  ;; Info fields are display-only, always considered "blank" for filtering purposes
  true)

(defmethod value-blank? :default [_] true)

;; ---

(defmulti value->text
  "Convert a template field value to plain text for display.
   Takes optional opts map for formatting (e.g., {:number-format fn, :date-format fn})."
  (fn [value & _opts] (->typename value)))

(defmethod value->text :IdeaTemplateTextField [value & _opts]
  (str (:text value)))

(defmethod value->text :IdeaTemplateNumberField [value & {:keys [number-format]}]
  (let [n (:number value)
        fmt (or number-format str)]
    (if n
      (fmt n)
      "")))

(defmethod value->text :IdeaTemplateDateField [value & {:keys [date-format]}]
  (let [d (:date value)
        fmt (or date-format str)]
    (fmt d)))

(defmethod value->text :IdeaTemplateUserField [value & _opts]
  (string/join ", " (map :fullName (:users value))))

(defn ideas-value->text
  "Plain-text rendering of an IDEAS field value (comma-separated linked-idea titles).
   Exposed as a plain fn so callers whose values lack a __typename tag (e.g. the Excel
   report) can share this logic without dispatching through value->text."
  [value]
  (string/join ", " (map :title (:ideas value))))

(defmethod value->text :IdeaTemplateIdeasField [value & _opts]
  (ideas-value->text value))

(defmethod value->text :IdeaTemplateSelectionField [value & _opts]
  (string/join ", "
               (for [{:keys [title selected]} (:options value)
                     :when selected]
                 title)))

(defmethod value->text :IdeaTemplateInfoField [_value & _opts]
  ;; Info fields are display-only, no user-entered value to convert
  "")

(defmethod value->text :default [value & _opts] (str "!!! " (pr-str value)))

;; ---

(defn some-value?
  "Returns true if the value is not blank. Opposite of value-blank?"
  [value]
  (not (value-blank? value)))

(defn mongo-field-value-empty?
  "Returns true if a raw MongoDB template field's value is nil/empty.
   Operates on the raw {:values ... :options ...} shape from the ideas collection.
   For USERS fields, checks the :options array; for all others, checks :values."
  [field field-type]
  (if (= :USERS (->type field-type))
    (let [opts (:options field)]
      (or (nil? opts) (empty? opts)))
    (let [v (:values field)]
      (or (nil? v)
          (and (string? v) (string/blank? v))
          (and (sequential? v) (empty? v))))))

;; =============================================================================
;; Import Value Conversion (CLJ only - backend import from Excel/CSV)
;; =============================================================================
;; Multimethod for converting string values from imports into mongo template items.
;; Dispatches on the mongo-type string (e.g., "title", "textarea", "number").

#?(:clj
   (do
     (defn- remove-newlines [s]
       (->> (string/split-lines s)
            (map string/trim)
            (string/join " ")))

     (defn- ->float [x]
       (when x
         (cond
           (and (string? x)
                (not (string/blank? x)))
           (try
             (parse-double x)
             (catch Exception _e nil))

           (and (string? x)
                (string/blank? x))
           nil

           (number? x)
           (float x)

           :else nil)))

     (defn- single-select-value [template-item string-value]
       (let [options (for [{:keys [title] :as option} (:options template-item)]
                       (if (= title string-value)
                         (assoc option :checked "checked")
                         (assoc option :checked "")))]
         (assoc template-item
                :options options
                :values (if string-value [string-value] []))))

     (defn- multi-select-value [template-item string-values-set]
       (let [options (for [{:keys [title] :as option} (:options template-item)]
                       (if (contains? string-values-set title)
                         (assoc option :checked "checked")
                         (assoc option :checked "")))
             values (vec (map :title (filter #(not= "" (:checked %)) options)))]
         (assoc template-item
                :options options
                :values values)))

     (defn multi-select-string->set
       "Parse a multi-select value into a set of strings.
        Accepts: nil, sequential collection, or semicolon-separated string."
       [s]
       (cond
         (nil? s)
         #{}

         ;; New behavior: client sends vectors (or seqs) of values
         (sequential? s)
         (->> s
              (map str)
              (map string/trim)
              (filter (complement string/blank?))
              set)

         ;; Backwards compatible: semicolon-separated string
         :else
         (->> (string/split (str s) #";")
              (map string/trim)
              (filter (complement string/blank?))
              set)))

     (defmulti string->template-value
       "Given a (stage) template-item and an incoming idea, return
        a template item that can be directly inserted into mongo.
        The stage template item must have been pre-populated with all the
        expected options, otherwise behaviour is undefined.
        Dispatches on the :type (mongo-type) of the template-item."
       (fn [template-item _idea]
         (:type template-item)))

     (defmethod string->template-value "title" [template-item idea]
       (assoc template-item :values (string/trim (html/html->plaintext
                                                  (remove-newlines
                                                   (str (:title idea)))))))

     (defmethod string->template-value "textarea" [template-item idea]
       ;; we don't do any processing here. The client is responsible
       ;; to prepare HTML if required
       (if-let [s (get-in idea [:template (:id template-item)])]
         (let [trimmed (string/trim (str s))]
           (if (string/blank? trimmed)
             template-item
             (assoc template-item :values trimmed)))
         template-item))

     (defmethod string->template-value "text" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (let [processed (string/trim
                          (html/html->plaintext
                           (remove-newlines (str s))))]
           (if (string/blank? processed)
             template-item
             (assoc template-item :values processed)))
         template-item))

     (defmethod string->template-value "number" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (if-some [v (->float s)]
           (assoc template-item :values v)
           template-item)
         template-item))

     (defmethod string->template-value "scale" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (if-some [v (->float s)]
           (assoc template-item :values v)
           template-item)
         template-item))

     (defmethod string->template-value "slider" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (if-some [v (->float s)]
           (assoc template-item :values v)
           template-item)
         template-item))

     (defmethod string->template-value "info" [template-item _idea]
       ;; Info fields are display-only, no value to import
       template-item)

     (defmethod string->template-value "date" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (try
           (if-some [inst (util/->inst s)]
             (assoc template-item :values inst)
             template-item)
           (catch Exception _e template-item))
         template-item))

     (defmethod string->template-value "users" [template-item _idea]
       template-item)

     (defmethod string->template-value "ideas" [template-item _idea]
       template-item)

     (defmethod string->template-value "select" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (let [trimmed (string/trim (str s))]
           (if (string/blank? trimmed)
             template-item
             (single-select-value template-item trimmed)))
         template-item))

     (defmethod string->template-value "radio" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (let [trimmed (string/trim (str s))]
           (if (string/blank? trimmed)
             template-item
             (single-select-value template-item trimmed)))
         template-item))

     (defmethod string->template-value "checkbox" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (let [value-set (multi-select-string->set s)]
           (if (empty? value-set)
             template-item
             (multi-select-value template-item value-set)))
         template-item))

     (defmethod string->template-value "checkbox_extensible" [template-item idea]
       (if-let [s (get-in idea [:template (:id template-item)])]
         (let [value-set (multi-select-string->set s)]
           (if (empty? value-set)
             template-item
             (multi-select-value template-item value-set)))
         template-item))

     (defn- select-template-item-markdown [options]
       (string/join ", " (for [option options
                               :when (:selected option)]
                           (:title option))))

     (defn- users-template-item-markdown [users]
       (string/join ", " (for [{:keys [fullName email]} users]
                           (format "%s (%s)" (str fullName) (str email)))))

     (defn- ideas-template-item-markdown [ideas]
       (string/join ", " (for [{:keys [title]} ideas]
                           (str title))))

     (defn idea-template->markdown
       "Returns a Markdown representation of the GraphQL idea template.
  Expects all GraphQL fields to be exhaustively selected."
       [base-uri template]
       (string/join
        (for [template-item template
              :let [template-type (-> template-item :ideaboxTemplateItem :type)
                    template-value (:value template-item)]
              :when (not (info-type? template-type))]
          (let [value (cond
                        (= template-type :TEXTAREA)
                        (->> (:html template-value) str (html/html->markdown base-uri))

                        (or (title-type? template-type)
                            (= template-type :TEXTLINE))
                        (-> (:text template-value) str)

                        (date-type? template-type)
                        (:date template-value)

                        (number-type? template-type)
                        (:number template-value)

                        (option-field-type? template-type)
                        (select-template-item-markdown (:options template-value))

                        (users-type? template-type)
                        (users-template-item-markdown (:users template-value))

                        (ideas-type? template-type)
                        (ideas-template-item-markdown (:ideas template-value))

                        :else
                        (throw (ex-info "Invalid template type" {:template-type template-type})))]
            (format "## %s\n\n%s\n\n"
                    (-> template-item :ideaboxTemplateItem :title)
                    value)))))

     ;; end of import value
     ))

;; ============================================================================
;; Migration Conversion Functions (Phase 1a — Field Data Migration v2)
;; ============================================================================
;; Pure functions for converting field values between types.
;; All return {:status :ok :value <converted>} or {:status :parse-failure :message <string>}

#?(:clj
   (do
     (defn convert-number->text
       "Convert a numeric value to formatted text string.
   Options: :decimalPlaces (0-3), :thousandSeparator (\"\" \",\" \".\" \" \"),
            :decimalSeparator (\".\" or \",\", default \".\")
   Assumes value is non-nil (caller checks emptiness)."
       [value {:keys [decimalPlaces thousandSeparator decimalSeparator]
               :or {decimalPlaces 0 thousandSeparator "" decimalSeparator "."}}]
       (let [n (double value)
             formatted (format (str "%." decimalPlaces "f") n)
             formatted (if (= "" thousandSeparator)
                         (string/replace formatted "." decimalSeparator)
                         (let [[int-part dec-part] (string/split formatted #"\." 2)
                               negative? (string/starts-with? int-part "-")
                               digits (if negative? (subs int-part 1) int-part)
                               grouped (->> (reverse digits)
                                            (partition-all 3)
                                            (map #(apply str (reverse %)))
                                            reverse
                                            (string/join thousandSeparator))
                               grouped (if negative? (str "-" grouped) grouped)]
                           (if dec-part
                             (str grouped decimalSeparator dec-part)
                             grouped)))]
         {:status :ok :value formatted}))

     (defn convert-scale->text
       "Convert a scale value to formatted text.
   scale-config has :min, :max, :labels [{:value N :label S}].
   Options: :scaleFormat — :PLAIN, :WITH_RANGE, :WITH_LABEL, :LABEL_ONLY
   Assumes value is non-nil (caller checks emptiness)."
       [value scale-config {:keys [scaleFormat] :or {scaleFormat :PLAIN}}]
       (let [n (long (Math/round (double value)))
             max-val (:max scale-config)
             label (scale-step-label scale-config n)]
         (if (and (nil? max-val) (#{:WITH_RANGE :WITH_LABEL} scaleFormat))
           {:status :parse-failure
            :message "Scale range unknown: the source field has no scale settings"}
           {:status :ok
            :value (case scaleFormat
                     :PLAIN (str n)
                     :WITH_RANGE (str n "/" max-val)
                     :WITH_LABEL (if label
                                   (str n "/" max-val " - " label)
                                   (str n "/" max-val))
                     :LABEL_ONLY (or label (str n)))})))

     (defn convert-slider->text
       "Convert a slider value to formatted text.
   slider-config has :min, :max, :sliderUnits, :sliderStep.
   Options: :includeUnits boolean
   Assumes value is non-nil (caller checks emptiness)."
       [value slider-config {:keys [includeUnits] :or {includeUnits false}}]
       (let [n (double value)
             formatted (if (== n (Math/floor n))
                         (str (long n))
                         (str n))
             units (when includeUnits (:sliderUnits slider-config ""))]
         {:status :ok
          :value (if (and includeUnits (not (string/blank? units)))
                   (str formatted units)
                   formatted)}))

     (defn convert-date->text
       "Convert a date value to formatted text.
   Value must be a java.util.Date or java.time.Instant (not nil, not a string).
   Options:
     :dateFormat — either a named style keyword (e.g. :short-date, :medium-date)
                   or a pattern string (e.g. \"dd/MM/yyyy\")
     :locale — locale string (e.g. \"en-gb\"); required when dateFormat is a keyword style
   Assumes value is non-nil (caller checks emptiness)."
       [value {:keys [dateFormat locale] :or {dateFormat "yyyy-MM-dd"}}]
       (if (keyword? dateFormat)
         ;; Named style — use locale-aware formatting
         {:status :ok :value (date-format/format-date value dateFormat (or locale "en-gb"))}
         ;; Pattern string — use DateTimeFormatter/ofPattern directly
         (let [inst (database/->inst value)
               zdt (.atZone inst (java.time.ZoneId/of "UTC"))
               formatter (java.time.format.DateTimeFormatter/ofPattern dateFormat)]
           {:status :ok :value (.format zdt formatter)})))

     (defn convert-text->number
       "Parse text into a number.
   Options: :stripNonNumeric (bool), :decimalSeparator (\".\" or \",\")
   Assumes value is non-nil and non-blank (caller checks emptiness)."
       [value {:keys [stripNonNumeric decimalSeparator]
               :or {stripNonNumeric false decimalSeparator "."}}]
       (let [s (string/trim (str value))
             s (if stripNonNumeric
                 (string/replace s (re-pattern (str "[^0-9\\-" (java.util.regex.Pattern/quote decimalSeparator) "]")) "")
                 s)
             s (if (= decimalSeparator ",")
                 (string/replace s "," ".")
                 s)]
         (if (string/blank? s)
           {:status :parse-failure :message (str "Cannot parse as number: \"" value "\"")}
           (if-let [n (parse-double s)]
             {:status :ok :value n}
             {:status :parse-failure :message (str "Cannot parse as number: \"" value "\"")}))))

     (defn convert-text->date
       "Parse text into a date (Instant).
   Options: :inputDateFormat — expected format of the source text, or \"auto\" for ISO-8601.
   Assumes value is non-nil and non-blank (caller checks emptiness)."
       [value {:keys [inputDateFormat] :or {inputDateFormat "auto"}}]
       (try
         (let [s (string/trim (str value))]
           (if (= inputDateFormat "auto")
             {:status :ok :value (java.time.Instant/parse s)}
             (let [formatter (-> (java.time.format.DateTimeFormatter/ofPattern inputDateFormat)
                                 (.withZone (java.time.ZoneId/of "UTC")))
                   temporal (java.time.LocalDate/parse s formatter)
                   inst (.toInstant (.atStartOfDay temporal (java.time.ZoneId/of "UTC")))]
               {:status :ok :value inst})))
         (catch Exception _
           {:status :parse-failure :message (str "Cannot parse as date: \"" value "\"")})))

     (defn convert-number->date
       "Interpret a number as a year → date (Jan 1 of that year).
   Only works for 4-digit numbers.
   Assumes value is non-nil (caller checks emptiness)."
       [value]
       (let [n (long (Math/round (double value)))]
         (if (and (>= n 1000) (<= n 9999))
           {:status :ok :value (java.time.Instant/parse (str n "-01-01T00:00:00Z"))}
           {:status :parse-failure :message (str "Not a valid year (must be 4 digits): " n)})))

     (defn convert-date->number
       "Extract year from a date value.
   Value must be a java.util.Date or java.time.Instant.
   Assumes value is non-nil (caller checks emptiness)."
       [value]
       (let [inst (database/->inst value)
             year (.getYear (.atZone inst (java.time.ZoneId/of "UTC")))]
         {:status :ok :value (double year)}))

     (defn extract-text-from-html
       "Extract plain text from HTML content.
        Delegates to nosco.util.html/html->plaintext (Jsoup-based)."
       [html-string]
       (if (or (nil? html-string) (string/blank? html-string))
         nil
         (html/html->plaintext html-string)))

     (defn convert-textline->textarea
       "Wrap plain text in a <p> tag for TEXTAREA storage.
   Assumes value is non-nil and non-blank (caller checks emptiness)."
       [value]
       {:status :ok :value (str "<p>" value "</p>")})

     (defn convert-textarea->textline
       "Extract plain text from HTML (TEXTAREA → TEXTLINE).
   Assumes value is non-nil and non-blank (caller checks emptiness)."
       [value]
       (let [text (extract-text-from-html value)]
         (if (string/blank? text)
           {:status :parse-failure :message "No text content in HTML"}
           {:status :ok :value text})))

     (defn compute-migration
       "Given source value, source type, target type, and options,
   compute the migrated value.
   Returns {:status :ok :value <converted>} or {:status :parse-failure :message <string>}.

   opts keys (all camelCase, passed from GraphQL format-options):
     :decimalPlaces, :thousandSeparator — for number→text
     :scaleFormat — for scale→text
     :includeUnits — for slider→text
     :dateFormat — for date→text
     :stripNonNumeric, :decimalSeparator — for text→number
     :inputDateFormat — for text→date
   Plus:
     :sourceFieldConfig — source field settings map (e.g. {:scale {:min :max :labels}})
     :targetFieldConfig — target field settings map (needed for NUMBER→SCALE validation)"
       [source-value source-type target-type opts]
       (let [source-type (->type source-type)
             target-type (->type target-type)
             source-field-config (:sourceFieldConfig opts)
             target-field-config (:targetFieldConfig opts)
             scale-config (:scale source-field-config)
             slider-config (:slider source-field-config)]
         (cond
           ;; Same type — direct copy. SCALE/SLIDER fall through so they hit the
           ;; bounds-aware branches below (source/target may have different ranges).
           (and (= source-type target-type)
                (not (#{:SCALE :SLIDER} target-type)))
           {:status :ok :value source-value}

           ;; NUMBER/SLIDER → SCALE — validate against target scale min/max
           (and (number-type? source-type) (= target-type :SCALE))
           (let [target-scale (:scale target-field-config)
                 min-val (:min target-scale)
                 max-val (:max target-scale)
                 clamp? (= :CLAMP (some-> (:outOfBoundsMode opts) name keyword))
                 n (long (Math/round (double source-value)))]
             (cond
               (and min-val (< n min-val))
               (if clamp?
                 {:status :ok :value (double min-val)}
                 {:status :parse-failure
                  :message (str "Value " n " is below scale minimum (" min-val ")")})

               (and max-val (> n max-val))
               (if clamp?
                 {:status :ok :value (double max-val)}
                 {:status :parse-failure
                  :message (str "Value " n " is above scale maximum (" max-val ")")})

               :else
               {:status :ok :value (double n)}))

           ;; NUMBER/SCALE → SLIDER — validate against target slider min/max
           (and (number-type? source-type) (= target-type :SLIDER))
           (let [target-slider (:slider target-field-config)
                 min-val (:min target-slider)
                 max-val (:max target-slider)
                 clamp? (= :CLAMP (some-> (:outOfBoundsMode opts) name keyword))
                 n (double source-value)]
             (cond
               (and min-val (< n (double min-val)))
               (if clamp?
                 {:status :ok :value min-val}
                 {:status :parse-failure
                  :message (str "Value " n " is below slider minimum (" min-val ")")})

               (and max-val (> n (double max-val)))
               (if clamp?
                 {:status :ok :value max-val}
                 {:status :parse-failure
                  :message (str "Value " n " is above slider maximum (" max-val ")")})

               :else
               {:status :ok :value source-value}))

           ;; Compatible numeric types (SCALE/SLIDER → NUMBER, etc) — direct copy
           (and (number-type? source-type) (number-type? target-type))
           {:status :ok :value source-value}

           ;; TEXTLINE → TEXTAREA
           (and (= source-type :TEXTLINE) (= target-type :TEXTAREA))
           (convert-textline->textarea source-value)

           ;; TEXTAREA → TEXTLINE
           (and (= source-type :TEXTAREA) (= target-type :TEXTLINE))
           (convert-textarea->textline source-value)

           ;; NUMBER/SCALE/SLIDER → TEXT (TEXTAREA or TEXTLINE)
           (and (= source-type :NUMBER) (text-type? target-type))
           (convert-number->text source-value opts)

           (and (= source-type :SCALE) (text-type? target-type))
           (convert-scale->text source-value scale-config opts)

           (and (= source-type :SLIDER) (text-type? target-type))
           (convert-slider->text source-value slider-config opts)

           ;; DATE → TEXT
           (and (= source-type :DATE) (text-type? target-type))
           (convert-date->text source-value opts)

           ;; TEXT → NUMBER/SCALE/SLIDER
           (and (text-type? source-type) (= target-type :SCALE))
           (let [text (if (= source-type :TEXTAREA)
                        (extract-text-from-html source-value)
                        source-value)
                 result (convert-text->number text opts)]
             (if (= :ok (:status result))
               (let [target-scale (:scale target-field-config)
                     min-val (:min target-scale)
                     max-val (:max target-scale)
                     clamp? (= :CLAMP (some-> (:outOfBoundsMode opts) name keyword))
                     n (long (Math/round (double (:value result))))]
                 (cond
                   (and min-val (< n min-val))
                   (if clamp?
                     {:status :ok :value (double min-val)}
                     {:status :parse-failure
                      :message (str "Parsed value " n " is below scale minimum (" min-val ")")})
                   (and max-val (> n max-val))
                   (if clamp?
                     {:status :ok :value (double max-val)}
                     {:status :parse-failure
                      :message (str "Parsed value " n " is above scale maximum (" max-val ")")})
                   :else
                   {:status :ok :value (double n)}))
               result))

           (and (text-type? source-type) (= target-type :SLIDER))
           (let [text (if (= source-type :TEXTAREA)
                        (extract-text-from-html source-value)
                        source-value)
                 result (convert-text->number text opts)]
             (if (= :ok (:status result))
               (let [target-slider (:slider target-field-config)
                     min-val (:min target-slider)
                     max-val (:max target-slider)
                     clamp? (= :CLAMP (some-> (:outOfBoundsMode opts) name keyword))
                     n (double (:value result))]
                 (cond
                   (and min-val (< n (double min-val)))
                   (if clamp?
                     {:status :ok :value min-val}
                     {:status :parse-failure
                      :message (str "Parsed value " n " is below slider minimum (" min-val ")")})
                   (and max-val (> n (double max-val)))
                   (if clamp?
                     {:status :ok :value max-val}
                     {:status :parse-failure
                      :message (str "Parsed value " n " is above slider maximum (" max-val ")")})
                   :else
                   {:status :ok :value (:value result)}))
               result))

           (and (text-type? source-type) (number-type? target-type))
           (let [text (if (= source-type :TEXTAREA)
                        (extract-text-from-html source-value)
                        source-value)]
             (convert-text->number text opts))

           ;; TEXT → DATE
           (and (text-type? source-type) (= target-type :DATE))
           (let [text (if (= source-type :TEXTAREA)
                        (extract-text-from-html source-value)
                        source-value)]
             (convert-text->date text opts))

           ;; NUMBER → DATE (year interpretation)
           (and (= source-type :NUMBER) (= target-type :DATE))
           (convert-number->date source-value)

           ;; DATE → NUMBER (extract year)
           (and (= source-type :DATE) (number-type? target-type))
           (convert-date->number source-value)

           ;; Unsupported conversion
           :else
           {:status :parse-failure
            :message (str "Unsupported conversion: " source-type " → " target-type)})))

     ;; end of conversion functions
     ))
